// Updates the date-range calendar's highlights and Start/End labels.
// Moving the pointer between periods needs immediate visual feedback. These helpers
// change the existing calendar elements directly so every visible month does not
// need to render again for each period the pointer passes over.
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import { DraftRange } from './draftRange.ts';
import { formatPeriodBoundary, PeriodIndexRange } from './periodRanges.ts';

export function parsePeriodIndex(element: HTMLElement | null): number | null {
    if (!element?.dataset.periodIndex) return null;
    const value = Number(element.dataset.periodIndex);
    const periodIndex = Number.isInteger(value) ? value : null;
    return periodIndex;
}

export function setVisualRange(
    cells: Map<number, HTMLElement>,
    previous: PeriodIndexRange | null,
    next: PeriodIndexRange | null,
) {
    if (sameRange(previous, next)) return;
    if (!previous || !next) {
        updateAllCellClasses(cells, next);
        return;
    }

    cells.get(previous.firstIndex)?.classList.remove('range-start');
    cells.get(previous.lastIndex)?.classList.remove('range-end');

    // When the preview's start or end moves, only periods between the old and new
    // positions enter or leave the highlighted range. Other calendar cells stay as they are.
    updateRangeDifference(cells, previous.firstIndex, next.firstIndex, true);
    updateRangeDifference(cells, previous.lastIndex, next.lastIndex, false);

    cells.get(next.firstIndex)?.classList.add('range-start');
    cells.get(next.lastIndex)?.classList.add('range-end');
}

function updateRangeDifference(
    cells: Map<number, HTMLElement>,
    previousIndex: number,
    nextIndex: number,
    isStart: boolean,
) {
    const from = Math.min(previousIndex, nextIndex);
    const to = Math.max(previousIndex, nextIndex);
    for (let index = from; index <= to; index += 1) {
        const isInRange = isStart
            ? index >= nextIndex
            : index <= nextIndex;
        cells.get(index)?.classList.toggle('in-range', isInRange);
    }
}

export function updateAllCellClasses(cells: Map<number, HTMLElement>, range: PeriodIndexRange | null) {
    for (const [index, element] of cells) {
        const isInRange = range != null && index >= range.firstIndex && index <= range.lastIndex;
        element.classList.toggle('in-range', isInRange);
        element.classList.toggle('range-start', range?.firstIndex === index);
        element.classList.toggle('range-end', range?.lastIndex === index);
    }
}

function sameRange(first: PeriodIndexRange | null, second: PeriodIndexRange | null): boolean {
    const isSameRange = first?.firstIndex === second?.firstIndex
        && first?.lastIndex === second?.lastIndex;
    return isSameRange;
}

export function updateBoundaryLabels(
    startElement: HTMLElement | null,
    endElement: HTMLElement | null,
    range: DraftRange,
    periods: PeriodOption[],
    resolution: Resolution,
) {
    if (startElement) {
        startElement.textContent = formatBoundary(range.firstIndex, periods, resolution, 'start');
    }
    if (endElement) {
        endElement.textContent = formatBoundary(range.lastIndex, periods, resolution, 'end');
    }
}

export function formatBoundary(
    index: number | null,
    periods: PeriodOption[],
    resolution: Resolution,
    boundary: 'start' | 'end',
): string {
    const label = index == null
        ? 'Not selected'
        : formatPeriodBoundary(periods[index], resolution, boundary);
    return label;
}

export function updateHoverTipText(element: HTMLDivElement | null, text: string) {
    if (!element) return;
    element.textContent = text;
    element.classList.add('visible');
}

export function updateHoverTipPosition(element: HTMLDivElement | null, x: number, y: number) {
    if (!element) return;
    element.style.left = `${x + 12}px`;
    element.style.top = `${y + 12}px`;
}

export function hideHoverTip(element: HTMLDivElement | null) {
    element?.classList.remove('visible');
}

export function getInitialPeriodClass(periodIndex: number, draft: DraftRange): string {
    if (periodIndex === -1 || draft.firstIndex == null) return '';
    const lastIndex = draft.lastIndex ?? draft.firstIndex;
    const classes = [];
    if (periodIndex >= draft.firstIndex && periodIndex <= lastIndex) classes.push('in-range');
    if (periodIndex === draft.firstIndex) classes.push('range-start');
    if (periodIndex === lastIndex) classes.push('range-end');
    const className = classes.join(' ');
    return className;
}
